import { supabase } from './supabase'

/**
 * Get storage limits for an organization
 * Checks custom override first, then subscription tier default
 */
export const getOrganizationStorageLimit = async (organizationId) => {
  try {
    // First check for custom override
    const { data: customLimit, error: customError } = await supabase
      .from('storage_limits')
      .select('*')
      .eq('organization_id', organizationId)
      .single()

    // Handle 406 (table doesn't exist or RLS blocking) or other errors gracefully
    if (customError && customError.code !== 'PGRST116') {
      // If table doesn't exist (406) or other error, skip and return defaults
      if (customError.status === 406 || customError.code === 'PGRST100') {
        console.warn('Storage limits table not accessible, using defaults')
        return {
          success: true,
          data: {
            maxStorageBytes: 1073741824, // 1GB default
            maxFileSizeBytes: 10485760, // 10MB default
            maxFilesPerOrganization: null, // Unlimited
            isCustom: false,
          },
        }
      }
    }

    if (customLimit && !customError) {
      return {
        success: true,
        data: {
          maxStorageBytes: customLimit.max_storage_bytes,
          maxFileSizeBytes: customLimit.max_file_size_bytes,
          maxFilesPerOrganization: customLimit.max_files_per_organization,
          isCustom: true,
        },
      }
    }

    // Get organization's subscription tier
    const { data: subscription, error: subError } = await supabase
      .from('organization_subscriptions')
      .select('tier_id, subscription_tiers(*)')
      .eq('organization_id', organizationId)
      .eq('status', 'active')
      .single()

    // Handle 406 for organization_subscriptions table
    if (subError && (subError.status === 406 || subError.code === 'PGRST100')) {
      console.warn('Organization subscriptions table not accessible, using defaults')
      return {
        success: true,
        data: {
          maxStorageBytes: 1073741824, // 1GB default
          maxFileSizeBytes: 10485760, // 10MB default
          maxFilesPerOrganization: null, // Unlimited
          isCustom: false,
        },
      }
    }

    if (subError || !subscription) {
      // No active subscription, return defaults
      return {
        success: true,
        data: {
          maxStorageBytes: 1073741824, // 1GB default
          maxFileSizeBytes: 10485760, // 10MB default
          maxFilesPerOrganization: null, // Unlimited
          isCustom: false,
        },
      }
    }

    // Get tier limits
    const { data: tierLimit, error: tierError } = await supabase
      .from('storage_limits')
      .select('*')
      .eq('subscription_tier_id', subscription.tier_id)
      .is('organization_id', null)
      .single()

    if (tierLimit && !tierError) {
      return {
        success: true,
        data: {
          maxStorageBytes: tierLimit.max_storage_bytes,
          maxFileSizeBytes: tierLimit.max_file_size_bytes,
          maxFilesPerOrganization: tierLimit.max_files_per_organization,
          isCustom: false,
        },
      }
    }

    // Return defaults if no tier limit found
    return {
      success: true,
      data: {
        maxStorageBytes: 1073741824, // 1GB default
        maxFileSizeBytes: 10485760, // 10MB default
        maxFilesPerOrganization: null, // Unlimited
        isCustom: false,
      },
    }
  } catch (error) {
    // Return defaults on any error
    console.warn('Error getting storage limit, using defaults:', error)
    return {
      success: true,
      data: {
        maxStorageBytes: 1073741824, // 1GB default
        maxFileSizeBytes: 10485760, // 10MB default
        maxFilesPerOrganization: null, // Unlimited
        isCustom: false,
      },
    }
  }
}

/**
 * Get current storage usage for an organization
 */
export const getOrganizationStorageUsage = async (organizationId) => {
  try {
    const { data, error } = await supabase
      .from('storage_usage')
      .select('*')
      .eq('organization_id', organizationId)
      .single()

    // Handle 406 (table doesn't exist or RLS blocking) gracefully
    if (error) {
      if (error.status === 406 || error.code === 'PGRST100') {
        console.warn('Storage usage table not accessible, returning zero usage')
        return {
          success: true,
          data: {
            totalBytesUsed: 0,
            totalFilesCount: 0,
            lastCalculatedAt: new Date().toISOString(),
          },
        }
      }
      if (error.code !== 'PGRST116') {
        throw error
      }
    }

    return {
      success: true,
      data: data || {
        total_bytes_used: 0,
        total_files_count: 0,
        last_calculated_at: new Date().toISOString(),
      },
    }
  } catch (error) {
    console.error('Error getting storage usage:', error)
    throw error
  }
}

/**
 * Record a file upload in storage tracking
 */
export const recordFileUpload = async (organizationId, filePath, fileSizeBytes, fileType, category, uploadedBy) => {
  try {
    const { error } = await supabase.from('file_storage_records').insert({
      organization_id: organizationId,
      file_path: filePath,
      file_size_bytes: fileSizeBytes,
      file_type: fileType,
      category: category,
      uploaded_by: uploadedBy,
    })

    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Error recording file upload:', error)
    throw error
  }
}

/**
 * Record a file deletion in storage tracking
 */
export const recordFileDeletion = async (organizationId, filePath) => {
  try {
    const { error } = await supabase
      .from('file_storage_records')
      .update({ deleted_at: new Date().toISOString() })
      .eq('organization_id', organizationId)
      .eq('file_path', filePath)
      .is('deleted_at', null)

    if (error) throw error
    return { success: true }
  } catch (error) {
    console.error('Error recording file deletion:', error)
    throw error
  }
}

/**
 * Check if organization can upload file (checks limits)
 */
export const canUploadFile = async (organizationId, fileSizeBytes) => {
  try {
    const [limitResult, usageResult] = await Promise.all([
      getOrganizationStorageLimit(organizationId),
      getOrganizationStorageUsage(organizationId),
    ])

    if (!limitResult.success || !usageResult.success) {
      return {
        canUpload: false,
        reason: 'Failed to check storage limits',
      }
    }

    const limits = limitResult.data
    const usage = usageResult.data

    // Check file size limit
    if (fileSizeBytes > limits.maxFileSizeBytes) {
      return {
        canUpload: false,
        reason: `File size exceeds maximum allowed size of ${formatBytes(limits.maxFileSizeBytes)}`,
      }
    }

    // Check total storage limit
    if (usage.total_bytes_used + fileSizeBytes > limits.maxStorageBytes) {
      return {
        canUpload: false,
        reason: `Upload would exceed storage limit of ${formatBytes(limits.maxStorageBytes)}`,
        currentUsage: usage.total_bytes_used,
        limit: limits.maxStorageBytes,
      }
    }

    // Check file count limit (if set)
    if (limits.maxFilesPerOrganization !== null && usage.total_files_count >= limits.maxFilesPerOrganization) {
      return {
        canUpload: false,
        reason: `Maximum file count of ${limits.maxFilesPerOrganization} reached`,
      }
    }

    return {
      canUpload: true,
      currentUsage: usage.total_bytes_used,
      limit: limits.maxStorageBytes,
      remainingBytes: limits.maxStorageBytes - usage.total_bytes_used,
    }
  } catch (error) {
    console.error('Error checking upload permission:', error)
    return {
      canUpload: false,
      reason: 'Error checking storage limits',
    }
  }
}

/**
 * Format bytes to human-readable string
 */
export const formatBytes = (bytes) => {
  if (!bytes || bytes === 0 || isNaN(bytes)) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

/**
 * Get storage usage history for an organization
 */
export const getStorageUsageHistory = async (organizationId, days = 30) => {
  try {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - days)

    const { data, error } = await supabase
      .from('storage_usage_history')
      .select('*')
      .eq('organization_id', organizationId)
      .gte('recorded_at', cutoffDate.toISOString())
      .order('recorded_at', { ascending: true })

    if (error) throw error
    return { success: true, data: data || [] }
  } catch (error) {
    console.error('Error getting storage usage history:', error)
    throw error
  }
}

